<template>
  <div>
    <div class="tabs flex border-b mb-4">
      <button
        class="px-1 py-2 -mb-px font-semibold border-b-2 px-4"
        :class="{
          'border-custom-yellow text-custom-blue':
            currentTab === 'triage-assessment',
          'text-gray-500': currentTab !== 'triage-assessment',
        }"
        @click="currentTab = 'triage-assessment'"
      >
        Triage Assessment
      </button>
      <button
        class="px-1 py-2 -mb-px font-semibold border-b-2 px-4"
        :class="{
          'border-custom-yellow text-custom-blue':
            currentTab === 'allergy-history',
          'text-gray-500': currentTab !== 'allergy-history',
        }"
        @click="currentTab = 'allergy-history'"
      >
        History & Allergy
      </button>
    </div>

    <div v-if="currentTab === 'triage-assessment'">
      <div class="grid grid-cols-2 px-4 gap-4 mb-6">
        <div>
          <p class="font-semibold">Reason for Visit:</p>
          <p>{{ triageAssessment?.visit_reason }}</p>
        </div>
        <div>
          <p class="font-semibold">Complaint/Symptoms:</p>
          <p>{{ triageAssessment?.complaint }}</p>
        </div>
        <div>
          <p class="font-semibold">Duration of Symptoms:</p>
          <p>
            {{ triageAssessment?.complaint_duration }}
          </p>
        </div>
        <div>
          <p class="font-semibold">Pain Level:</p>
          <p>
            {{ triageAssessment?.pain_level }}
          </p>
        </div>
        <div>
          <p class="font-semibold">Risk of fall:</p>
          <p>
            {{ triageAssessment?.risk_of_fall }}
          </p>
        </div>
      </div>

      <h4 class="font-semibold mt-8 mb-2 text-yellow-500">Glasgow Coma Scale</h4>
      <div class="grid grid-cols-2 px-4 gap-4 mb-6">
        <div>
          <p class="font-semibold">Eye Response:</p>
          <p>{{ triageGlasgowComaScale?.eye_response }}</p>
        </div>
        <div>
          <p class="font-semibold">Motor Response:</p>
          <p>{{ triageGlasgowComaScale?.motor_response }}</p>
        </div>
        <div>
          <p class="font-semibold">Verbal Response:</p>
          <p>{{ triageGlasgowComaScale?.verbal_response }}</p>
        </div>
        <div>
          <p class="font-semibold">Patient Triage Code:</p>
          <span
            :class="`${getTriageColor(
              triageGlasgowComaScale?.score
            )} px-6 rounded`"
            >{{ triageGlasgowComaScale?.score }}</span
          >
        </div>
      </div>

      <!-- CTAS -->
      <h4 class="font-semibold mt-8 mb-2 text-blue-600">
        Canadian Triage and Acuity Scale (CTAS)
      </h4>
      <div class="grid grid-cols-2 px-4 gap-4 mb-6">
        <div>
          <p class="font-semibold">CTAS Level:</p>
          <span
            v-if="triageAssessment?.ctas_level"
            :class="`${getCtasColor(triageAssessment.ctas_level)} px-4 py-1 rounded text-sm font-bold`"
          >
            {{ getCtasLabel(triageAssessment.ctas_level) }}
          </span>
          <span v-else class="text-gray-400 text-sm">Not recorded</span>
        </div>
      </div>
    </div>

    <div v-if="currentTab === 'allergy-history'">
      <div class="grid grid-cols-2 px-4 gap-4 mb-6">
        <div>
          <p class="font-semibold">Food Allergy:</p>
          <p>
            <span v-for="(allergy, index) in food_allergies" :key="allergy.id">
              {{ allergy.name
              }}<span v-if="index < food_allergies.length - 1">, </span>
            </span>
          </p>
        </div>
        <div>
          <p class="font-semibold">Drug Allergy:</p>
          <span v-for="(allergy, index) in drug_allergies" :key="allergy.id">
            {{ allergy.name
            }}<span v-if="index < drug_allergies.length - 1">, </span>
          </span>
        </div>
        <div>
          <p class="font-semibold">Past Medical History:</p>
          <span v-for="(history, index) in medical_histories" :key="history.id">
            {{ history.name
            }}<span v-if="index < medical_histories.length - 1">, </span>
          </span>
        </div>
        <div>
          <p class="font-semibold">Current Medication:</p>
          <span
            v-for="(medicine, index) in current_medication"
            :key="medicine.id"
          >
            {{ medicine.name
            }}<span v-if="index < current_medication.length - 1">, </span>
          </span>
        </div>
        <div>
          <p class="font-semibold">Drug History:</p>
          <span v-for="(history, index) in drug_histories" :key="history.id">
            {{ history.name
            }}<span v-if="index < drug_histories.length - 1">, </span>
          </span>
        </div>
        <div>
          <p class="font-semibold">Family History:</p>
          <span v-for="(history, index) in family_histories" :key="history.id">
            {{ history.name
            }}<span v-if="index < family_histories.length - 1">, </span>
          </span>
        </div>
        <div>
          <p class="font-semibold">Surgical History:</p>
          <span v-for="(history, index) in surgical_histories" :key="history.id">
            {{ history.name }}<span v-if="index < surgical_histories.length - 1">, </span>
          </span>
        </div>
        <div>
          <p class="font-semibold">Triage Medicine:</p>
          <span
            v-for="(medicine, index) in triage_medication"
            :key="medicine.id"
          >
            {{ medicine.name
            }}<span v-if="index < triage_medication.length - 1">, </span>
          </span>
        </div>
      </div>
      <!-- Gynaecological & Obstetric History -->
      <template v-if="gynae_history">
        <h4 class="font-semibold mt-6 mb-3 text-purple-600 border-t pt-4">Gynaecological & Obstetric History</h4>
        <div class="grid grid-cols-2 px-4 gap-4 mb-6">
          <div>
            <p class="font-semibold">Last Menstrual Period (LMP):</p>
            <p>{{ gynae_history.lmp || '—' }}</p>
          </div>
          <div>
            <p class="font-semibold">Pregnancy Status:</p>
            <p>{{ gynae_history.is_pregnant }}</p>
          </div>
          <div v-if="gynae_history.is_pregnant === 'yes'">
            <p class="font-semibold">Expected Delivery Date (EDD):</p>
            <p>{{ gynae_history.edd || '—' }}</p>
          </div>
          <div>
            <p class="font-semibold">GPAL:</p>
            <p>
              G{{ gynae_history.gravida ?? '—' }}
              P{{ gynae_history.para ?? '—' }}
              A{{ gynae_history.abortus ?? '—' }}
              L{{ gynae_history.living_children ?? '—' }}
            </p>
          </div>
          <div>
            <p class="font-semibold">Menopausal Status:</p>
            <p>{{ gynae_history.menopausal_status }}</p>
          </div>
          <div v-if="gynae_history.contraceptive_method">
            <p class="font-semibold">Contraceptive Method:</p>
            <p>{{ gynae_history.contraceptive_method }}</p>
          </div>
          <div v-if="gynae_history.notes" class="col-span-2">
            <p class="font-semibold">Notes:</p>
            <p>{{ gynae_history.notes }}</p>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: "TriageAssessment",
  props: {
    triageAssessment: {
      type: Object,
    },
    triageGlasgowComaScale: {
      type: Object,
    },
    drug_allergies: {
      type: Array,
    },
    food_allergies: {
      type: Array,
    },
    drug_histories: {
      type: Array,
    },
    medical_histories: {
      type: Array,
    },
    family_histories: {
      type: Array,
    },
    surgical_histories: {
      type: Array,
    },
    triage_medication: {
      type: Array,
    },
    current_medication: {
      type: Array,
    },
    gynae_history: {
      type: Object
    }
  },
  data() {
    return {
      currentTab: "triage-assessment",
    };
  },
  computed: {
    getTriageColor() {
      return (triageCode) => {
        switch (triageCode) {
          case "red":
            return "bg-red-500 text-white";
          case "orange":
            return "bg-custom-yellow text-white";
          case "yellow":
            return "bg-yellow-200 text-white";
          case "green":
            return "bg-green-500 text-white";
          case "blue":
            return "bg-blue-500 text-white";
          default:
            return "";
        }
      };
    },
    getCtasColor() {
      return (level) => {
        switch (String(level)) {
          case "1": return "bg-blue-600 text-white";
          case "2": return "bg-red-500 text-white";
          case "3": return "bg-yellow-400 text-yellow-900";
          case "4": return "bg-green-500 text-white";
          case "5": return "bg-gray-200 text-gray-800";
          default:  return "bg-gray-100 text-gray-500";
        }
      };
    },
    getCtasLabel() {
      return (level) => {
        const labels = { "1": "I — Resuscitation", "2": "II — Emergent", "3": "III — Urgent", "4": "IV — Less Urgent", "5": "V — Non-Urgent" };
        return labels[String(level)] || "—";
      };
    },
  },
};
</script>
